import StoryblokClient from 'storyblok-js-client';
import { PHASE_PRODUCTION_BUILD } from 'next/constants';
import type { AxiosError } from 'axios';
import type {
  StoriesParams,
  StoryblokResult,
  StoryData,
  StoryblokComponent,
} from 'storyblok-js-client';
import {
  getDefaultParams,
  getStoryApiVersion,
  stripAllCasesResponse,
  stripLocationsBlockResponse,
  stripNextCaseResponse,
  stripResponse,
} from './api.utils';
import { StoryCdnPath, StorySlug } from './types/api.types';
import type { Case, StoryContent } from './types/global.types';
import config from '../../locale.config';
import type { LocationBlokData } from '../../components/blocks/location/Location.data';

// getCountryFolderNameByLocale,
// getLanguageByLocale
/**
 * https://www.storyblok.com/docs/api/content-delivery?utm_source=github.com&utm_medium=readme&utm_campaign=storyblok-js-client
 */
const storyBlokClientApi = new StoryblokClient({
  accessToken: `${process.env.STORYBLOK_API_KEY}`,
  cache: {
    clear: 'auto',
    type: 'memory',
  },
});

export default storyBlokClientApi;

const getStoryblok = async (
  ...args: Parameters<StoryblokClient['get']>
): Promise<StoryblokResult> => {
  try {
    return await storyBlokClientApi.get(...args);
  } catch (error) {
    const requestError = error as (Partial<AxiosError> & { status?: number }) | null;
    const status = requestError?.response?.status ?? requestError?.status;
    const code = requestError?.code;
    const detail = status ? `HTTP ${status}` : code || 'network error';
    const hint =
      status === 401
        ? ' Check STORYBLOK_API_KEY; draft content requires a valid preview access token.'
        : '';

    // Next.js sends getStaticPaths errors between processes. Axios request/response
    // objects are circular, so preserve only serializable diagnostics and the 404 status.
    throw Object.assign(new Error(`Storyblok ${args[0]} failed (${detail}).${hint}`), {
      status,
      code,
    });
  }
};

export const isForcedPreview = process.env.NODE_ENV === 'development'; // false; // process.env.NODE_ENV === 'development';

let buildSpaceRequest: Promise<StoryblokResult> | undefined;

export const getSpace = async (preview?: boolean): Promise<StoryblokResult> => {
  const usePreview = preview || isForcedPreview;
  const loadSpace = () =>
    getStoryblok(StoryCdnPath.Space, {
      cv: new Date(),
      version: getStoryApiVersion(usePreview),
    });

  // NEXT_PHASE is set by Next.js in its build workers, not in the deployed server.
  // Keep runtime revalidation, browser navigation, and draft previews fresh.
  if (
    typeof window !== 'undefined' ||
    process.env.NEXT_PHASE !== PHASE_PRODUCTION_BUILD ||
    usePreview
  ) {
    return loadSpace();
  }

  // Share both in-flight and completed requests for this build worker's lifetime.
  if (!buildSpaceRequest) {
    buildSpaceRequest = loadSpace().catch((error) => {
      buildSpaceRequest = undefined;
      throw error;
    });
  }

  return buildSpaceRequest;
};

export const fetchAllLinks = async (
  folderName?: string,
  preview?: boolean,
): Promise<StoryblokResult> => {
  const params: {
    starts_with?: string;
  } = {};
  if (folderName) {
    params.starts_with = `${folderName}/`;
  }

  return getStoryblok(StoryCdnPath.Links, {
    ...getDefaultParams(preview || isForcedPreview),
    ...params,
  });
};

export const fetchStory = async (
  storyId: string | undefined,
  params?: StoriesParams,
  preview?: boolean,
  noPathIdOnly: boolean = false,
  shouldStripResponse = true,
): Promise<StoryblokResult> => {
  const mergedParams = params || {};
  let path = storyId;

  const country = undefined; // config.defaultLocale; // config.getCountryFolderNameByLocale(params?.language);
  if (country && !noPathIdOnly) {
    path = `${country}/${storyId}`;
  }

  mergedParams.language = config.getLanguageByLocale(params?.language);

  try {
    const res = await getStoryblok(`${StoryCdnPath.Stories}/${path}`, {
      ...getDefaultParams(preview || isForcedPreview),
      ...mergedParams,
    });
    if (shouldStripResponse) return stripResponse(res);
    return res;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const fetchStories = (
  params?: StoriesParams,
  preview?: boolean,
): Promise<StoryblokResult> => {
  const mergedParams = params || {};
  mergedParams.language = config.getLanguageByLocale(params?.language);

  return getStoryblok(`${StoryCdnPath.Stories}/`, {
    ...getDefaultParams(preview || isForcedPreview),
    ...mergedParams,
  });
};

export const getRecentNews = async (
  excluding_slugs?: string,
  params?: StoriesParams,
  preview?: boolean,
  story: string = StorySlug.News,
): Promise<StoryblokResult> =>
  fetchStories(
    {
      excluding_slugs: `${excluding_slugs},${StorySlug.News}/`,
      per_page: 100,
      starts_with: `${story}/`,
      // starts_with: `${config.defaultLocale}/${StorySlug.News}/`,
      // starts_with: `${config.getCountryFolderNameByLocale(params?.language)}/${StorySlug.News}/`,
      excluding_fields: 'modules',
      sort_by: 'position:asc',
      ...params,
    },
    preview,
  );

export const getAllCases = async (
  params?: StoriesParams,
  preview?: boolean,
): Promise<Array<StoryData<Case>> | undefined> => {
  try {
    return stripAllCasesResponse(
      await fetchStories(
        {
          starts_with: `${StorySlug.Cases}/`,
          excluding_fields: 'modules',
          sort_by: 'position:asc',
          resolve_relations: 'case.locations',
          // 100 cases is not ALL cases but storyblok currently doesnt offer a 'all' option. At the time of writing this comment
          // there are about 50 cases, so if it gets bigger than 100 we'll need to rewrite the logic here
          per_page: 100,
          ...params,
        },
        preview,
      ),
    );
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getNextCase = async (
  uuid: string,
  params?: StoriesParams,
  excluding_slugs?: string,
  preview?: boolean,
  story: string = StorySlug.Cases,
): Promise<StoryData<StoryContent & StoryblokComponent<string>> | undefined> => {
  const response = await fetchStory(
    StorySlug.Work,
    {
      resolve_relations: 'case_listing',
      language: params?.language,
    },
    preview,
  );

  const caseListing = response.data.story.content.modules.find(
    (m: StoryblokComponent<string>) => m.component === 'case_listing',
  );

  const currentCaseIndex = caseListing.cards.findIndex((c: { case: string }) => c.case === uuid);

  const nextCaseId = (currentCaseIndex + 1) % caseListing.cards.length;
  const nextCaseStoryId = caseListing.cards[nextCaseId].case;

  const nextCaseResponse = await fetchStories(
    {
      starts_with: `${story}/`,
      by_uuids: nextCaseStoryId,
      excluding_fields: 'modules',
      sort_by: 'position:asc',
      resolve_relations: 'case.locations',
      ...params,
    },
    preview,
  );

  return stripNextCaseResponse(nextCaseResponse.data.stories[0]);
};

export const getLocationsList = async (
  params?: StoriesParams,
  preview?: boolean,
  story: string = `${StorySlug.Locations}`,
): Promise<Array<LocationBlokData> | undefined> => {
  try {
    const response = await fetchStory(
      story,
      {
        ...params,
      },
      preview,
      false,
      false,
    );

    const locationsBlok = response?.data?.story?.content?.modules?.find(
      (module: StoryblokComponent<string>) => module.component === 'locations_blok',
    );

    return stripLocationsBlockResponse(locationsBlok);
  } catch (error) {
    return Promise.reject(error);
  }
};
